import React from 'react';
import { Rnd } from 'react-rnd';
import useEditorStore from '../../store/editorStore';
import { blockTypes, dragAnimationConfig } from '../../config/constants';
import LogoBlock from './LogoBlock';
import TextBlock from './TextBlock';
import FrameBlock from './FrameBlock';
import DetailBlock from './DetailBlock';
import ImageBlock from './ImageBlock';
import QRCodeBlock from './QRCodeBlock';
import './Block.css';

function Block({ block }) {
  const { 
    updateBlock, 
    selectBlock, 
    selectedBlockId, 
    grid, 
    sections, 
    checkBlockInSection 
  } = useEditorStore();
  const isSelected = selectedBlockId === block.id;
  const [isDragging, setIsDragging] = React.useState(false);

  const handleDragStart = () => {
    setIsDragging(true);
    if (!isSelected) {
      selectBlock(block.id);
    }
  };

  const handleDragStop = (e, d) => {
    setIsDragging(false);
    // Check if block is now inside any section
    let parentSectionId = null;
    let snapPosition = { x: d.x, y: d.y };

    for (const section of sections) {
      const tempBlock = { ...block, x: d.x, y: d.y };
      if (checkBlockInSection(tempBlock, section)) {
        parentSectionId = section.id;
        
        // Auto-snap to header layout slots
        if (section.type === 'header') {
          const layout = section.styles?.layout || 'logo-text';
          const padding = 16;
          const gap = 12;
          
          // Calculate slot positions based on layout
          if (layout === 'logo-text' && block.type === blockTypes.LOGO) {
            // Snap logo to left slot
            snapPosition.x = section.x + padding;
            snapPosition.y = section.y + padding;
            snapPosition.width = (section.width - padding * 2 - gap) / 2;
          } else if (layout === 'logo-text' && block.type === blockTypes.TEXT) {
            // Snap text to right slot
            snapPosition.x = section.x + (section.width / 2) + (gap / 2);
            snapPosition.y = section.y + padding;
            snapPosition.width = (section.width - padding * 2 - gap) / 2;
          } else if (layout === 'text-logo' && block.type === blockTypes.TEXT) {
            // Snap text to left slot
            snapPosition.x = section.x + padding;
            snapPosition.y = section.y + padding;
            snapPosition.width = (section.width - padding * 2 - gap) / 2;
          } else if (layout === 'text-logo' && block.type === blockTypes.LOGO) {
            // Snap logo to right slot
            snapPosition.x = section.x + (section.width / 2) + (gap / 2);
            snapPosition.y = section.y + padding;
            snapPosition.width = (section.width - padding * 2 - gap) / 2;
          } else if (layout === 'text-only' && block.type === blockTypes.TEXT) {
            // Snap text to full width
            snapPosition.x = section.x + padding;
            snapPosition.y = section.y + padding;
            snapPosition.width = section.width - padding * 2;
          } else if (layout === 'logo-only' && block.type === blockTypes.LOGO) {
            // Snap logo to full width
            snapPosition.x = section.x + padding;
            snapPosition.y = section.y + padding;
            snapPosition.width = section.width - padding * 2;
          }
        }
        
        break;
      }
    }

    updateBlock(block.id, {
      ...snapPosition,
      parentSectionId,
    });
  };

  const handleResizeStop = (e, direction, ref, delta, position) => {
    updateBlock(block.id, {
      width: parseInt(ref.style.width),
      height: parseInt(ref.style.height),
      x: position.x,
      y: position.y,
    });
  };

  const handleClick = (e) => {
    e.stopPropagation();
    selectBlock(block.id);
  };

  const snapGrid = grid.snapEnabled ? [grid.size, grid.size] : [1, 1];

  const renderBlockContent = () => {
    switch (block.type) {
      case blockTypes.LOGO:
        return <LogoBlock block={block} />;
      case blockTypes.TEXT:
        return <TextBlock block={block} />;
      case blockTypes.FRAME:
        return <FrameBlock block={block} />;
      case blockTypes.DETAIL:
        return <DetailBlock block={block} />;
      case blockTypes.IMAGE:
        return <ImageBlock block={block} />;
      case blockTypes.QRCODE:
        return <QRCodeBlock block={block} />;
      default:
        return null;
    }
  };

  return (
    <Rnd
      position={{ x: block.x, y: block.y }}
      size={{ width: block.width, height: block.height }}
      onDragStart={handleDragStart}
      onDragStop={handleDragStop}
      onResizeStop={handleResizeStop}
      onClick={handleClick}
      bounds="parent"
      dragGrid={snapGrid}
      resizeGrid={snapGrid}
      className={`block ${isSelected ? 'selected' : ''}`}
      style={{
        zIndex: block.zIndex,
        transform: `rotate(${block.rotation}deg)`,
        transition: isDragging ? 'none' : dragAnimationConfig.transition,
      }}
      enableResizing={isSelected}
      disableDragging={false}
    >
      {renderBlockContent()}
      {isSelected && <div className="block-selection-outline" />}
    </Rnd>
  );
}

export default Block;
